import { AlertCircle, X } from '@untitledui/icons';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Header, ListBoxSection } from 'react-aria-components';

import { Badge } from '@/lib/base/badges';
import { Select } from '@/lib/base/forms/select/select';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { FilterCondition, OperatorType } from '@/lib/crm/filters/types';
import type { FieldTypeSchema } from '@/lib/crm/hooks/useFilterSchema';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import {
  decodeAssocPropKey,
  encodeAssocPropKey,
} from '@/lib/crm/utils/assocPropKey';
import { logger } from '@/lib/logger';
import type { CrmObject, FilterValue } from '@/types/crm';
import { formatRelativeDateValue, isRelativeDateValue } from '@/types/reports';

import { AssociationConditionBody } from './AssociationConditionBody';
import { PropertyValueInput } from './PropertyValueInput';
import {
  type FieldSection,
  getAvailableOperators,
  needsValueInput,
  OPERATOR_LABELS,
  renderFieldSelectEmptyState,
  SECTION_HEADER_CLASS,
  valueForOperatorChange,
} from './shared';

interface FilterConditionRowProps {
  condition: FilterCondition;
  fieldSections: FieldSection[];
  fieldFilter: (textValue: string, inputValue: string) => boolean;
  propertyTypeMap: Record<string, PropertyTypeSchema>;
  fieldTypeMap: Record<string, FieldTypeSchema>;
  associationTypeLookup: Record<string, AssociationType>;
  /** Pre-fetched target property maps keyed by association_type_name. */
  targetPropertyMaps: Record<string, Record<string, PropertyTypeSchema>>;
  teamId: number;
  invalidConditionIds: Set<string>;
  onUpdate: (id: string, updates: Partial<FilterCondition>) => void;
  onRemove: (id: string) => void;
  onKeyDown: React.KeyboardEventHandler;
  onAssociationObjectsChange?: (objects: Record<string, CrmObject>) => void;
}

export const FilterConditionRow = React.memo(function FilterConditionRow({
  condition,
  fieldSections,
  fieldFilter,
  propertyTypeMap,
  fieldTypeMap,
  associationTypeLookup,
  targetPropertyMaps,
  teamId,
  invalidConditionIds,
  onUpdate,
  onRemove,
  onKeyDown,
  onAssociationObjectsChange,
}: FilterConditionRowProps) {
  const isInvalid = invalidConditionIds.has(condition.id);
  const isAssociationCondition = !!condition.association;
  const property = condition.field ? propertyTypeMap[condition.field] : null;
  const operatorSelectRef = useRef<HTMLDivElement>(null);

  // A relative date window (a saved report's rolling "Last 30 days") that did
  // not fold back into the wizard's date-range dropdown. Its value is a token
  // resolved server-side, not something any value input can edit — rendering
  // the normal input would show an empty box and the first keystroke would
  // destroy the token. So the value is shown read-only and left untouched, which
  // keeps a re-save lossless.
  //
  // Gated strictly on the value itself: every non-report surface using this
  // shared row (pipeline board, CRM object list, meeting summaries) never sees
  // a token, so their rendering is bit-identical to before.
  const relativeDateLabel = isRelativeDateValue(condition.value)
    ? formatRelativeDateValue(condition.value)
    : null;

  // The combobox filters items but preserves section order, so an incidental
  // property match (e.g. "Unmatched Owner") can outrank the section the user
  // actually meant (the "Owner" association). Track the live query and hoist
  // any section whose own name matches it above the rest, so a name/section
  // match is prioritised over a match buried in a section's properties.
  const [fieldQuery, setFieldQuery] = useState('');
  const orderedFieldSections = useMemo(() => {
    const query = fieldQuery.trim().toLowerCase();
    if (!query) {
      return fieldSections;
    }
    const nameMatches: FieldSection[] = [];
    const rest: FieldSection[] = [];
    for (const section of fieldSections) {
      if (section.label.toLowerCase().includes(query)) {
        nameMatches.push(section);
      } else {
        rest.push(section);
      }
    }
    // Stable partition: sections keep their relative order within each group.
    return nameMatches.length > 0 ? [...nameMatches, ...rest] : fieldSections;
  }, [fieldSections, fieldQuery]);

  // Property-path operators. Association conditions render their own operator
  // select inside AssociationConditionBody (record vs target-property modes).
  const availableOperators = useMemo(
    () => (property ? getAvailableOperators(property, fieldTypeMap) : []),
    [property, fieldTypeMap]
  );

  const operatorItems = useMemo(
    () =>
      availableOperators.map(op => ({
        id: op,
        label: OPERATOR_LABELS[op],
      })),
    [availableOperators]
  );

  // Resolve combobox selectedKey for all three condition shapes:
  // - association + target_property → assoc_prop:{assoc}:{prop}
  // - association only → assoc:{assoc}
  // - property → the property_type_name
  let selectedFieldKey: string | null;
  if (
    isAssociationCondition &&
    condition.association &&
    condition.target_property
  ) {
    selectedFieldKey = encodeAssocPropKey(
      condition.association,
      condition.target_property
    );
  } else if (isAssociationCondition) {
    selectedFieldKey = `assoc:${condition.association}`;
  } else if (condition.field) {
    selectedFieldKey = condition.field;
  } else {
    selectedFieldKey = null;
  }

  const hasFieldSelected = !!condition.field || isAssociationCondition;

  const handleFieldQueryChange = useCallback(
    (value: string) => setFieldQuery(value),
    []
  );

  const handleFieldSelectionChange = useCallback(
    (key: React.Key | null) => {
      if (key === null) {
        return;
      }
      const keyStr = key as string;
      // `error:` is a reserved sentinel for the non-selectable "Properties
      // unavailable" placeholder shown when a target-property fetch fails.
      // It is never a real field key, so ignore it defensively even though
      // the item is rendered disabled.
      if (keyStr.startsWith('error:')) {
        return;
      }
      if (keyStr.startsWith('assoc_prop:')) {
        const knownAssocNames = fieldSections
          .filter(s => s.key.startsWith('assoc:'))
          .map(s => s.key.slice('assoc:'.length));
        const decoded = decodeAssocPropKey(keyStr, knownAssocNames);
        if (!decoded) {
          logger.error('Could not decode assoc_prop key', undefined, {
            keyStr,
            knownAssocNames,
          });
          return;
        }
        const { association: assocName, property: propName } = decoded;
        const targetMap = targetPropertyMaps[assocName] ?? {};
        const prop = targetMap[propName];
        const ops = prop ? getAvailableOperators(prop, fieldTypeMap) : [];
        onUpdate(condition.id, {
          field: '',
          association: assocName,
          target_property: propName,
          operator: ops[0] || 'equals',
          value: null,
        });
      } else if (keyStr.startsWith('assoc:')) {
        // Record-based association filter (e.g. "Specific company")
        const assocName = keyStr.slice(6);
        onUpdate(condition.id, {
          field: '',
          association: assocName,
          target_property: undefined,
          operator: 'equals',
          value: [],
        });
      } else {
        const newProperty = propertyTypeMap[keyStr];
        const newOperators = newProperty
          ? getAvailableOperators(newProperty, fieldTypeMap)
          : [];
        onUpdate(condition.id, {
          field: keyStr,
          association: undefined,
          target_property: undefined,
          operator: newOperators[0] || 'equals',
          value: null,
        });
      }
      setTimeout(() => {
        operatorSelectRef.current?.querySelector('button')?.focus();
      }, 0);
    },
    [
      condition.id,
      onUpdate,
      propertyTypeMap,
      fieldTypeMap,
      targetPropertyMaps,
      fieldSections,
    ]
  );

  const handleOperatorChange = useCallback(
    (key: React.Key | null) => {
      if (key === null) {
        return;
      }
      const newOperator = key as OperatorType;
      onUpdate(condition.id, {
        operator: newOperator,
        value: valueForOperatorChange(condition.value, newOperator),
      });
    },
    [condition.id, condition.value, onUpdate]
  );

  const handleRemove = useCallback(
    () => onRemove(condition.id),
    [condition.id, onRemove]
  );

  const handlePropertyValueChange = useCallback(
    (newValue: FilterValue) => onUpdate(condition.id, { value: newValue }),
    [condition.id, onUpdate]
  );

  const associationType = condition.association
    ? associationTypeLookup[condition.association]
    : undefined;

  // Resolve the target property map for this condition's association
  const targetPropMap = useMemo(
    () =>
      condition.association
        ? (targetPropertyMaps[condition.association] ?? {})
        : {},
    [condition.association, targetPropertyMaps]
  );

  // Rows are chrome-less — the surrounding group card is the container.
  // Boxing every row (bg + border) double-framed the whole builder; the
  // condition now reads as a sentence: field / operator / value. Invalid
  // rows keep a red ring as the error affordance.
  return (
    <div
      className={`flex gap-1.5 items-start rounded-lg ${isInvalid ? 'p-1.5 ring-1 ring-utility-error-300 ring-inset' : ''}`}
    >
      <div className='flex-1 min-w-0 flex flex-col gap-1.5'>
        {/* No helptext above the combobox — its placeholder ("Search
            properties and associations...") already says what it searches,
            and the two-line hint pushed every new row into a tower. */}
        <Select.ComboBox
          aria-label='Filter property'
          placeholder='Search properties and associations'
          shortcut={false}
          defaultFilter={fieldFilter}
          selectedKey={selectedFieldKey}
          onSelectionChange={handleFieldSelectionChange}
          onInputChange={handleFieldQueryChange}
          renderEmptyState={renderFieldSelectEmptyState}
        >
          {orderedFieldSections.map(section => (
            <ListBoxSection key={section.key}>
              <Header className={SECTION_HEADER_CLASS}>{section.label}</Header>
              {section.items.map(item => (
                <Select.Item
                  key={item.id}
                  id={item.id}
                  icon={item.icon}
                  supportingText={item.supportingText}
                  textValue={item.textValue}
                  isDisabled={item.isDisabled}
                >
                  {item.label}
                </Select.Item>
              ))}
            </ListBoxSection>
          ))}
        </Select.ComboBox>

        {hasFieldSelected &&
          (isAssociationCondition ? (
            <AssociationConditionBody
              condition={condition}
              associationType={associationType}
              teamId={teamId}
              targetPropertyTypeMap={targetPropMap}
              fieldTypeMap={fieldTypeMap}
              operatorSelectRef={operatorSelectRef}
              onUpdate={onUpdate}
              onKeyDown={onKeyDown}
              onAssociationObjectsChange={onAssociationObjectsChange}
            />
          ) : (
            property && (
              <>
                {/* Operator + value share one line where the width allows
                    (wrapping on narrow rails) — three stacked full-width
                    controls made each condition a ~150px tower. The operator
                    sizes to its label (never truncates — "Starts wi…" reads
                    as broken); the value input takes the remaining width and
                    wraps to its own line when there isn't enough. */}
                <div className='flex flex-wrap items-start gap-1.5'>
                  <div className='flex-none'>
                    <Select
                      ref={operatorSelectRef}
                      aria-label='Filter operator'
                      selectedKey={condition.operator}
                      onSelectionChange={handleOperatorChange}
                      items={operatorItems}
                    >
                      {item => (
                        <Select.Item id={item.id}>{item.label}</Select.Item>
                      )}
                    </Select>
                  </div>

                  {needsValueInput(condition.operator) &&
                    (relativeDateLabel ? (
                      <div className='flex min-w-40 flex-1 basis-40 items-center'>
                        <Badge type='pill-color' color='gray' size='sm'>
                          {relativeDateLabel}
                        </Badge>
                      </div>
                    ) : (
                      <div
                        className='min-w-40 flex-1 basis-40'
                        onKeyDown={onKeyDown}
                      >
                        <PropertyValueInput
                          property={property}
                          operator={condition.operator}
                          value={condition.value}
                          onChange={handlePropertyValueChange}
                        />
                      </div>
                    ))}
                </div>
                {isInvalid && (
                  <p className='text-xs text-utility-error-600 flex items-center gap-1'>
                    <AlertCircle className='w-3 h-3 shrink-0' />
                    Invalid value for this field
                  </p>
                )}
              </>
            )
          ))}
      </div>

      <button
        className='p-1 mt-2 hover:bg-bg-secondary_hover rounded-md transition-colors text-secondary hover:text-primary shrink-0 cursor-pointer'
        onClick={handleRemove}
        aria-label='Remove filter condition'
      >
        <X className='w-4 h-4' />
      </button>
    </div>
  );
});
